/**
 * @sazeo/sdk — Company Events hooks
 *
 * TanStack Query wrappers for the company-events addon. All calls route through
 * the Edge Function at `/addons/company-events/*` guarded by
 * requireAddonEnabled() and enforced by RLS via the caller's JWT.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CompanyEvent {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  kind: 'all_hands' | 'team_building' | 'training' | 'holiday' | 'social' | 'other';
  location: string | null;
  starts_at: string;
  ends_at: string;
  all_day: boolean;
  capacity: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EventRsvp {
  id: string;
  tenant_id: string;
  event_id: string;
  user_id: string;
  status: 'attending' | 'maybe' | 'declined';
  created_at: string;
  updated_at: string;
}

export interface CompanyEventCreateInput {
  title: string;
  description?: string | null;
  kind?: CompanyEvent['kind'];
  location?: string | null;
  starts_at: string;
  ends_at: string;
  all_day?: boolean;
  capacity?: number | null;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const companyEventKeys = {
  all: ['company-events'] as const,
  list: (tenantId: string) => [...companyEventKeys.all, 'list', tenantId] as const,
  upcoming: (tenantId: string) => [...companyEventKeys.all, 'upcoming', tenantId] as const,
} as const;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useCompanyEvents(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: companyEventKeys.list(tenantId),
    queryFn: () => apiFetch<CompanyEvent[]>(supabase, 'addons/company-events/events'),
    enabled: Boolean(tenantId),
  });
}

export function useUpcomingEvents(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: companyEventKeys.upcoming(tenantId),
    queryFn: () => apiFetch<CompanyEvent[]>(supabase, 'addons/company-events/events/upcoming'),
    enabled: Boolean(tenantId),
  });
}

export function useCreateCompanyEvent(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyEventCreateInput) =>
      apiFetch<CompanyEvent>(supabase, 'addons/company-events/events', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyEventKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: companyEventKeys.upcoming(tenantId) });
    },
  });
}

export function useUpdateCompanyEvent(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CompanyEventCreateInput> }) =>
      apiFetch<CompanyEvent>(supabase, `addons/company-events/events/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyEventKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: companyEventKeys.upcoming(tenantId) });
    },
  });
}

export function useDeleteCompanyEvent(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(supabase, `addons/company-events/events/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyEventKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: companyEventKeys.upcoming(tenantId) });
    },
  });
}

export function useRsvp(supabase: SupabaseClientLike, tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, status }: { eventId: string; status: EventRsvp['status'] }) =>
      apiFetch<EventRsvp>(supabase, `addons/company-events/events/${eventId}/rsvp`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: companyEventKeys.list(tenantId) });
      void qc.invalidateQueries({ queryKey: companyEventKeys.upcoming(tenantId) });
    },
  });
}
